import { useRef } from "react";
import { Box, VStack, useDisclosure, BoxProps, useBoolean } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { EnumCommentEntityType } from "src/api/client/comment-v2.api.types";
import { Button } from "src/components/base/button";
import { removeDuplicates } from "src/components/common/comments-area/utils/remove-duplicates";
import { WriteCommentModal } from "src/components/modals/write-comment-modal/write-comment-modal";
import { ErrorState } from "src/components/pages/detail/shared/error-state";
import {
    INewsWriteCommentModalSaveParams,
    NewsWriteCommentModalContent,
} from "src/components/pages/news/components/news-write-comment-modal-content";
import { IUser } from "src/types/common";
import { CommentV2, TEditParams, TReplyParams } from "./comment/comment";
import { CommentAreaEmptyState } from "./comment-area-empty-state";
import { CommentBlockLoadingState } from "./comments-area-loading-state";
import { useAddComment } from "./hooks/use-add-comment";
import { useComments } from "./hooks/use-comments";
import { useUpdateComment } from "./hooks/use-update-comment";
import { resolveCommentsLength } from "./utils/resolve-comments-length";

export interface ICommentsAreaProps extends BoxProps {
    entityId: string;
    user: IUser | null;
    isCurrentUserQualifiedCommenter: boolean;
}

const CHARACTER_COUNT_LIMIT = 1500;

type TMessageToEdit = { message: string | null; commentId: string | null };
const messageToEditEmpty = { message: null, commentId: null };

export const CommentsArea = ({ entityId, user, isCurrentUserQualifiedCommenter, ...boxProps }: ICommentsAreaProps) => {
    const { t } = useTranslation(["common", "comment"]);
    const messageToEditRef = useRef<TMessageToEdit>(messageToEditEmpty);
    const modalProps = useDisclosure();
    const [isEditMode, setIsEditMode] = useBoolean();

    const { fetchNextPage, setParentComment, hasNextPage, totalComments, isError, isSuccess, comments, parentComment, isLoading } = useComments({
        entityId,
        modalProps,
    });

    const onPostCommentSuccess = () => {
        modalProps.onClose();
    };

    const { addComment, isLoading: isLoadingAddComment } = useAddComment({ onSuccess: onPostCommentSuccess, comments, entityId });
    const updateCommentMutation = useUpdateComment(entityId);

    const onReplyTo = ({ commentId, message }: TReplyParams) => {
        setParentComment({
            commentId,
            message,
        });
        modalProps.onOpen();
    };

    const handleOnCommentSave = (saveParams: INewsWriteCommentModalSaveParams) => {
        const { message, parentCommentId } = saveParams;

        if (isEditMode && messageToEditRef.current.commentId) {
            return updateCommentMutation.mutatePromise({ message, commentId: messageToEditRef.current.commentId }).finally(() => {
                setIsEditMode.off();
                modalProps.onClose();
            });
        }

        return addComment({
            entityReferences: [
                {
                    entityType: EnumCommentEntityType.ExternalNewsArticle,
                    entityId,
                },
            ],
            message,
            parentCommentId,
        });
    };

    const commentsCount = comments ? totalComments - resolveCommentsLength(comments) : 0;

    const handleOnClose = () => {
        messageToEditRef.current = messageToEditEmpty;
        modalProps.onClose();
        setIsEditMode.off();
    };

    const onEditOpen = ({ parentCommentId, commentId, message, parentPreviewMessage }: TEditParams) => {
        if (parentCommentId && parentPreviewMessage) {
            setParentComment({
                commentId: parentCommentId,
                message: parentPreviewMessage,
            });
        }

        messageToEditRef.current = { message, commentId: commentId };

        setIsEditMode.on();
        modalProps.onOpen();
    };

    return (
        <Box {...boxProps}>
            {user && isCurrentUserQualifiedCommenter && (
                <Box mb={[4, 5]}>
                    <WriteCommentModal
                        trader={user}
                        modalProps={{
                            ...modalProps,
                            onClose: handleOnClose,
                        }}
                    >
                        <NewsWriteCommentModalContent
                            content={isEditMode && messageToEditRef.current.message ? messageToEditRef.current.message : ""}
                            characterCountLimit={CHARACTER_COUNT_LIMIT}
                            onSave={handleOnCommentSave}
                            editorPlaceholder={t("placeholder.comment-area", { ns: "comment" })}
                            isPendingSave={updateCommentMutation.isLoading || isLoadingAddComment}
                            parentComment={parentComment}
                            entityId={entityId}
                        />
                    </WriteCommentModal>
                </Box>
            )}
            {isSuccess && comments && (
                <VStack spacing={2} w="100%" align="stretch">
                    {removeDuplicates(comments).map(comment => (
                        <CommentV2
                            onEdit={onEditOpen}
                            isCurrentUserQualifiedCommenter={isCurrentUserQualifiedCommenter}
                            currentUserId={user?.id}
                            entityId={entityId}
                            key={comment.commentId}
                            comment={comment}
                            onReply={onReplyTo}
                            showReplies
                            indent
                        />
                    ))}
                    {hasNextPage && commentsCount > 0 && (
                        <Button variant="outline" size="sm" display="block" mx="auto" alignSelf="center" onClick={() => fetchNextPage()}>
                            {t("buttons.load-more-comments", { ns: "comment", 0: commentsCount })}
                        </Button>
                    )}
                </VStack>
            )}
            {isSuccess && comments?.length === 0 && <CommentAreaEmptyState />}
            {isLoading && <CommentBlockLoadingState mt={[3, 3, 4]} />}
            {isError && (
                <ErrorState>
                    <CommentBlockLoadingState sx={{ div: { animation: "none" } }} />
                </ErrorState>
            )}
        </Box>
    );
};
